import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "./firebase";
import CryptoJS from "crypto-js";

// Use a strong 256-bit key for AES-256 encryption
const ENCRYPTION_KEY =
    process.env.REACT_APP_ENCRYPTION_KEY ||
    CryptoJS.lib.WordArray.random(32).toString();

interface Partner {
    id: string;
    name: string;
    accessCode: string;
    displayName: string;
    displayInfo: string;
    logo: string;
}

interface EncryptedPartner {
    id: string;
    encryptedName: string;
    encryptedAccessCode: string;
    displayName: string;
    displayInfo: string;
    logo: string;
}

// Cache for storing partner data with encryption
class PartnerCache {
    private static instance: PartnerCache;
    private cache: Map<string, EncryptedPartner> = new Map();
    private initializationPromise: Promise<void> | null = null;

    private constructor() {}

    static getInstance(): PartnerCache {
        if (!PartnerCache.instance) {
            PartnerCache.instance = new PartnerCache();
        }
        return PartnerCache.instance;
    }

    // Encrypt partner data before storing
    private encryptPartner(partner: Partner): EncryptedPartner {
        return {
            id: partner.id,
            encryptedName: encrypt(partner.name),
            encryptedAccessCode: encrypt(partner.accessCode),
            displayName: partner.displayName,
            displayInfo: partner.displayInfo,
            logo: partner.logo,
        };
    }

    // Initialize cache if not already initialized
    async initialize(): Promise<void> {
        if (this.initializationPromise) {
            return this.initializationPromise;
        }

        this.initializationPromise = this.fetchAndCachePartners();
        return this.initializationPromise;
    }

    private async fetchAndCachePartners(): Promise<void> {
        try {
            const partnersRef = collection(db, "partners");
            const querySnapshot = await getDocs(partnersRef);

            querySnapshot.forEach((doc) => {
                const data = doc.data() as Partner;
                // console.log("data", data);
                const encryptedPartner = this.encryptPartner({
                    ...data,
                    id: doc.id,
                });
                this.cache.set(data.name, encryptedPartner);
            });

            // console.log("cache", this.cache);
        } catch (error) {
            console.error("Error fetching partners:", error);
            throw error;
        }
    }

    // Get partner by name
    getPartner(partnerName: string): EncryptedPartner | null {
        return this.cache.get(partnerName) || null;
    }

    // Verify access code
    verifyAccessCode(partnerName: string, accessCode: string): boolean {
        const partner = this.cache.get(partnerName);
        if (!partner) {
            return false;
        }

        const decryptedAccessCode = decrypt(partner.encryptedAccessCode);
        return decryptedAccessCode === accessCode;
    }

    // Check if cache is initialized
    isInitialized(): boolean {
        return this.cache.size > 0;
    }
}

// Encryption functions
const encrypt = (text: string): string => {
    return CryptoJS.AES.encrypt(text, ENCRYPTION_KEY).toString();
};

const decrypt = (ciphertext: string): string => {
    const bytes = CryptoJS.AES.decrypt(ciphertext, ENCRYPTION_KEY);
    return bytes.toString(CryptoJS.enc.Utf8);
};

// Export singleton instance methods
export const partnerCache = PartnerCache.getInstance();

export const verifyAccessCodeLocally = (
    partnerName: string,
    accessCode: string
): boolean => {
    return partnerCache.verifyAccessCode(partnerName, accessCode);
};

export const getPartnerInfo = (
    partnerName: string
): EncryptedPartner | null => {
    return partnerCache.getPartner(partnerName);
};

export const initializePartnerCache = (): Promise<void> => {
    return partnerCache.initialize();
};
